import { isFuture, isValid, parseISO } from 'date-fns';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import isURL from 'validator/es/lib/isURL';

import i18n from 'shared/lib/i18n';

import { formatRole, getIsReadOnlyAdmin, toSentenceCase } from '../../utils';
import { calculateDuration } from '../Events/utils';

import { COMMUNITY_IMAGES_LIMIT } from '../Community/constants';
import { SR_IMAGES_LIMIT } from '../ServiceRequest/constants';

export const validateEmail = (email) => {
  if (!email) {
    return null;
  }
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email) ? null : i18n.t('errors.invalidEmail');
};

export const validatePhoneNumber = (value) => {
  if (!value) return null;
  const re = /^[+]{0,1}\d+$/g;
  return re.test(value) ? null : i18n.t('errors.phoneNumber');
};

export const validatePassword = (value) => {
  if (!value) return null;
  const reAnySpace = /[\s\r\n\u0085\u2028\u2029]+/g;
  if (reAnySpace.test(value)) {
    return i18n.t('errors.noPasswordSpaces');
  }
  const rePassword = /^(?=.*[A-Za-z])(?=.*[0-9])[A-Za-z\d!@#$%^&*]{8,}$/im;
  return rePassword.test(value) ? null : i18n.t('errors.password');
};

export const required = (value) => {
  if (!value || isNil(value) || (typeof value === 'object' && isEmpty(value))) {
    return i18n.t('errors.required');
  }
  return undefined;
};

const KB = 1024;
export const MB = KB * KB;

export const getBytesInMegabytes = (value) => MB * value;

export const requiredAndMaxSizeLimit = (value) => {
  const maxSize = 10;
  if (!value || isNil(value) || isEmpty(value)) {
    return i18n.t('errors.required');
  }
  if (value[0].size > getBytesInMegabytes(maxSize)) {
    return i18n.t('errors.fileSize', { limitMB: maxSize });
  }
  return undefined;
};

export const notNullOrUndefined = (value) => {
  if (value === null || value === undefined) {
    return i18n.t('errors.valueRequired');
  }
  return undefined;
};

export const validateRadius = (value) => {
  if (!value) return undefined;
  if (Number.isNaN(Number(value)) || value <= 0 || value > 10000)
    return i18n.t('errors.radiusRange');
  if (!Number.isInteger(Number(value))) return i18n.t('errors.radius');
  return undefined;
};

export const validateUrl = (value) => {
  if (!value || isURL(String(value))) {
    return undefined;
  }
  return i18n.t('errors.validUrl');
};

export const validateUrlOrTelOrSms = (value) => {
  const regex = /^(sms|tel):.{1,18}$/;
  if (!value || isURL(String(value)) || regex.test(value)) {
    return undefined;
  }
  return i18n.t('errors.validUrl');
};

export const validateHttpsUrl = (value) => {
  if (
    !value ||
    isURL(String(value), { require_protocol: true, protocols: ['https'] })
  ) {
    return undefined;
  }
  return i18n.t('errors.httpsRequired');
};

export const validateAllowedHours = (value, values) => {
  const { allowedTimeStart, allowedTimeEnd } = values;
  if (allowedTimeStart && !allowedTimeEnd) {
    return i18n.t('errors.endTimeRequired');
  }
  if (!allowedTimeStart && allowedTimeEnd) {
    return i18n.t('errors.startTimeRequired');
  }
  return undefined;
};

export const validateDuration = (value, values) => {
  const { startTime, endTime } = values;
  const duration = calculateDuration(startTime, endTime);
  if (duration <= 0) {
    return i18n.t('errors.durationError');
  }
  return undefined;
};

export const validateDate = (value) => {
  if (!value) return null;
  const date = parseISO(value);
  if (!isValid(date)) return i18n.t('errors.invalidDate');
  return null;
};

export const validateFutureDate = (value) => {
  if (!value) return null;
  const date = parseISO(value);
  if (!isFuture(date)) return i18n.t('errors.invalidDate');
  return null;
};

function IPtoNum(ip) {
  return Number(
    ip
      .split('.')
      .map((d) => `000${d}`.substr(-3))
      .join(''),
  );
}

export const validateIP = (value, values, props) => {
  if (value) {
    const re =
      /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    if (!re.test(value)) {
      return i18n.t('errors.invalidIP');
    }

    const { start_ip, end_ip } = values;
    if (start_ip && end_ip && re.test(start_ip) && re.test(end_ip)) {
      const lastStartDot = start_ip.lastIndexOf('.');
      const lastEndDot = end_ip.lastIndexOf('.');
      if (start_ip.slice(0, lastStartDot) !== end_ip.slice(0, lastEndDot)) {
        return value === end_ip && i18n.t('errors.ipAddressCount');
      }
      const { existingIpRanges } = props;
      const existingRange = existingIpRanges.find(
        ({ startIp, endIp }) =>
          IPtoNum(startIp) <= IPtoNum(value) &&
          IPtoNum(endIp) >= IPtoNum(value),
      );
      if (existingRange) {
        const { startIp, endIp, name } = existingRange;
        return i18n.t('errors.ipRange', { startIp, endIp, name });
      }
    }
  }
  return undefined;
};

export const formatSelect = (options, labelKey, valueKey) => {
  if (!options?.length) return [];
  return options.map((option) => ({
    label: labelKey ? option[labelKey] : option.name,
    value: valueKey ? option[valueKey] : option.id,
  }));
};

export const formatFilterSelect = (
  filterOptions,
  options,
  labelKey,
  valueKey,
) => {
  const filteredOptions = filterOptions?.length
    ? options?.filter(({ id }) => !filterOptions?.includes(id))
    : options;
  return filteredOptions
    ? formatSelect(filteredOptions, labelKey, valueKey)
    : [];
};

export const formatSelectWithFixedValues = (options) => {
  if (!options?.length) return [];
  return options.map((option = {}) => {
    const { name, id, defaultInBuilding } = option;
    return {
      label: name,
      value: id,
      isFixed: defaultInBuilding,
    };
  });
};

export const formatDeliverySelect = (options) => {
  if (!options) return [];
  return options.map((option) => ({
    label: option.name,
    value: option.id,
    icon: option?.icons?.[0]?.url,
  }));
};

export const formatTextSelect = (options) => {
  if (!options) return [];
  return options.map((option) => ({
    label: toSentenceCase(option),
    value: option,
  }));
};

export const formatSelectPersonAndRole = (options, propertyIds, roleTypes) => {
  if (!options) return [];
  return options.map((option) => {
    const allRoles = Object.assign([], option.residentRoles, option.staffRoles);
    const roleId = get(
      allRoles.find((r) => propertyIds.indexOf(Number(r.propertyId) > -1)),
      'role',
    );
    const role = roleId
      ? roleTypes.find((rt) => Number(rt.id) === Number(roleId))
      : null;
    return {
      label: `${option.firstName} ${option.lastName} - ${formatRole(
        get(role, 'name', ''),
      )}`,
      value: option.id,
    };
  });
};

export const doorDelay = (value) => {
  if (value < 0 || value > 120) {
    return i18n.t('errors.doorDelay');
  }
  return undefined;
};

// TODO: consider removing since mui select will be used
export const checkFixedValue = (value) => value.filter((v) => v?.isFixed);

export const formatReactSelectValue = (value, multi) => {
  if (!value) return null;
  let formattedValue = null;
  if (multi) {
    formattedValue = value
      .map((item) => item?.value)
      .filter((item) => item !== null && item !== undefined);
  } else {
    formattedValue = value?.value ?? null;
  }
  return formattedValue;
};

export const formatFileSize = (number) => {
  if (number < KB) {
    return `${number} ${i18n.t('metaInfo.bytes')}`;
  }
  if (number >= KB && number < MB) {
    return `${(number / KB).toFixed(1)} ${i18n.t('metaInfo.kilobytes')}`;
  }
  if (number >= MB) {
    return `${(number / MB).toFixed(1)} ${i18n.t('metaInfo.megabytes')}`;
  }
  return undefined;
};

export const maxFileSize = (files) => {
  const limitMB = 10;
  if (!files) {
    return undefined;
  }
  let error = null;
  files?.forEach((file) => {
    const size = file?.file?.size ?? file?.size;
    if (size > getBytesInMegabytes(limitMB)) {
      error = i18n.t('errors.fileSize', { limitMB });
    }
  });
  return error;
};

const validateFileLimit = (files, maxCount) => {
  if (files?.length > maxCount) {
    return i18n.t('errors.imagesMaxCount', { maxCount });
  }
  return null;
};

export const communityImagesLimit = (files) =>
  validateFileLimit(files, COMMUNITY_IMAGES_LIMIT);

export const srImagesLimit = (files) =>
  validateFileLimit(files, SR_IMAGES_LIMIT);

export const onlyNumeric = (value) => {
  if (!value) return null;
  const re = /^[0-9]*$/;
  return re.test(value) ? null : i18n.t('errors.positiveInteger');
};

export const facilityCodeRange = (value) =>
  value >= 0 && value <= 255 ? null : i18n.t('errors.facilityCodeRange');

export const cardNumberRange = (value) =>
  value >= 0 && value <= 65535 ? null : i18n.t('errors.cardNumberRange');

export const allowedCardNumber = (value, values, props) => {
  const { batch } = props;
  const { accessCardBatch, number } = values;
  if (!accessCardBatch || !number) return undefined;
  const selectedBatch =
    accessCardBatch &&
    Array.isArray(batch) &&
    batch.find((item) => item.id === accessCardBatch);
  if (!selectedBatch) return undefined;
  const { firstCardNumber, lastCardNumber } = selectedBatch;
  return Number(firstCardNumber) <= number && +lastCardNumber >= number
    ? null
    : i18n.t('errors.rangeValues', { firstCardNumber, lastCardNumber });
};

export const lastCardNumberValidation = (value, values) => {
  if (values.lastCardNumber <= values.firstCardNumber) {
    return i18n.t('errors.firstCardNumber');
  }
  return undefined;
};

export const validateReportDates = (value, values = {}) => {
  const { startDate, endDate } = values;
  if (value && startDate && endDate) {
    if (startDate > endDate && value === startDate)
      return i18n.t('errors.endDateAfterStart');
  }
  return undefined;
};

const lengthValidation = (value, limit) => {
  if (!value || !value?.length || !limit) return null;
  return value.length > limit
    ? i18n.t('errors.charactersLimit', { limit })
    : null;
};

const lengthValidation50 = (value) => lengthValidation(value, 50);
const lengthValidation60 = (value) => lengthValidation(value, 60);
const lengthValidation64 = (value) => lengthValidation(value, 64);
const lengthValidation120 = (value) => lengthValidation(value, 120);
const lengthValidation200 = (value) => lengthValidation(value, 200);
const lengthValidation250 = (value) => lengthValidation(value, 250);
const lengthValidation255 = (value) => lengthValidation(value, 255);
const lengthValidation500 = (value) => lengthValidation(value, 500);
const lengthValidation1000 = (value) => lengthValidation(value, 1000);
const lengthValidation1500 = (value) => lengthValidation(value, 1500);
const lengthValidation2000 = (value) => lengthValidation(value, 2000);

lengthValidation50.limit = 50;
lengthValidation60.limit = 60;
lengthValidation64.limit = 64;
lengthValidation120.limit = 120;
lengthValidation200.limit = 200;
lengthValidation250.limit = 250;
lengthValidation255.limit = 255;
lengthValidation500.limit = 500;
lengthValidation1000.limit = 1000;
lengthValidation1500.limit = 1500;
lengthValidation2000.limit = 2000;

export {
  lengthValidation50,
  lengthValidation60,
  lengthValidation64,
  lengthValidation120,
  lengthValidation200,
  lengthValidation250,
  lengthValidation255,
  lengthValidation500,
  lengthValidation1000,
  lengthValidation1500,
  lengthValidation2000,
};

export const getCharacterLimit = (validators) => {
  if (validators?.length) {
    const lengthValidator = validators.find((validator) => validator.limit);
    if (lengthValidator?.limit) {
      return lengthValidator.limit;
    }
    return undefined;
  }
  return undefined;
};

export const floorValidation = (value) => {
  if (value) {
    return /^\d+$/.test(value?.replace(' ', ''))
      ? null
      : i18n.t('errors.singleNumber');
  }
  return undefined;
};

export const replacePropsBasedOnPermissions = (props) => {
  const isReadOnlyAdmin = getIsReadOnlyAdmin();
  if (!isReadOnlyAdmin) return props;
  const safeProps = {
    ...props,
    canBeUpdated: false,
    disabled: true,
    isHideSubmitButton: true,
    customSubmit: () => {},
  };
  return safeProps;
};
